import {
  Box,
  Table,
  Text,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  BoxProps,
  Icon,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Switch,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { RiEyeLine } from 'react-icons/ri';
import { PiWarningCircle } from 'react-icons/pi';
import { LinkButton } from '../../../../../../../components/LinkButton';
import { Pagination } from '../../../../../../../components/Pagination';
import {
  TableFilterOption,
  TableFilters,
} from '../../../../../../../components/TableFilters';
import { useAuth } from '../../../../../../../hooks/auth';
import { IUserListItem } from '../../../../../../../services/Users/ListUsersService';
import AccountStatusBadge from '../../../../components/AccountStatusBadge';
import FaceIdStatusBadge from '../../../../components/FaceIdStatusBadge';
import OnboardStatusBadge from '../../../../components/OnboardStatusBadge';
import { VentureAssignmentActions } from '../../../../components/VentureAssignmentActions';

export interface IMember extends IUserListItem {
  formattedDate: string;
}

type MemberRestriction = NonNullable<IUserListItem['effectiveRestriction']>;

const getRestrictionLabel = (restriction: MemberRestriction): string => {
  const statusLabel = restriction.isBlocked ? 'bloqueado' : 'suspenso';

  if (restriction.source === 'SELF') {
    return restriction.isBlocked ? 'Bloqueado' : 'Suspenso';
  }

  const sourceLabel =
    restriction.source === 'TITULAR' ? 'Titular' : 'Responsável';
  const sourceName = restriction.sourceUserName
    ? ` (${restriction.sourceUserName})`
    : '';

  return `${sourceLabel}${sourceName} ${statusLabel}`;
};

interface IMemberTableProps extends BoxProps {
  members: IMember[];
  currentPage?: number;
  filterBy?: string;
  onChangeFilterBy?: (value: string) => void;
  onFilter?: (value: string) => void;
  onOrder?: (order: 'ASC' | 'DESC') => void;
  onPageChange?: (page: number) => void;
  onSort?: (sort: 'cpf' | 'name' | 'phone' | 'email' | 'createdAt') => void;
  onToggleBlockedOrSuspended?: (value: boolean) => void;
  order?: 'ASC' | 'DESC';
  isBlockedOrSuspended?: boolean;
  showDeleted?: boolean;
  onToggleShowDeleted?: () => void;
  onAssignmentChanged?: () => void;
  sortBy?: string;
  totalPages?: number;
  totalItems?: number;
}

export const MemberTable = ({
  members,
  currentPage,
  filterBy,
  onChangeFilterBy,
  onFilter,
  onOrder,
  onPageChange,
  onSort,
  onToggleBlockedOrSuspended,
  order,
  isBlockedOrSuspended = false,
  showDeleted = false,
  onToggleShowDeleted,
  onAssignmentChanged,
  sortBy,
  totalPages,
  totalItems,
  ...rest
}: IMemberTableProps): JSX.Element => {
  const { venture: authenticatedVenture, experienceLevelsTitleMap } = useAuth();

  const filterByOptions = useMemo<TableFilterOption[]>(
    () => [
      {
        label: 'Nome',
        value: 'name',
      },
      {
        label: 'E-mail',
        value: 'email',
      },
      {
        label: 'Telefone',
        value: 'phone',
      },
      {
        label: 'CPF',
        value: 'cpf',
        maskType: 'cpf',
      },
      {
        label: 'Experiência',
        value: 'experience',
      },
    ],
    [],
  );

  const filterSelectOptions = [
    {
      label: 'Todos',
      value: -1,
    },
    {
      label: 'Nível 1 e 2',
      value: 0,
    },
    {
      label: 'Nível 3 e 4',
      value: 1,
    },
    {
      label: 'Nível 5',
      value: 2,
    },
    {
      label: 'Nível 6',
      value: 3,
    },
    {
      label: 'Nível 6 e 7',
      value: 4,
    },
    {
      label: 'Nível 7',
      value: 5,
    },
    {
      label: 'Nível 8',
      value: 6,
    },
    {
      label: 'Especial',
      value: 7,
    },
  ];

  const sortByOptions = useMemo<TableFilterOption[]>(
    () => [
      {
        label: 'Nome',
        value: 'name',
      },
      {
        label: 'Telefone',
        value: 'phone',
      },
      {
        label: 'E-mail',
        value: 'email',
      },
      {
        label: 'CPF',
        value: 'cpf',
        maskType: 'cpf',
      },
      {
        label: 'Data de cadastro',
        value: 'createdAt',
      },
    ],
    [],
  );

  return (
    <Box {...rest}>
      <Flex align="flex-end" justify="space-between">
        <HStack align="flex-end" spacing="8">
          <TableFilters
            filterBy={filterBy}
            filterByOptions={filterByOptions}
            filterInputType={filterBy === 'experience' ? 'select' : 'input'}
            filterSelectOptions={filterSelectOptions}
            onChangeFilterBy={onChangeFilterBy}
            onFilter={onFilter}
            onOrder={onOrder}
            onSort={(sort) =>
              !!onSort &&
              onSort(sort as 'cpf' | 'name' | 'phone' | 'email' | 'createdAt')
            }
            order={order}
            sortBy={sortBy}
            sortByOptions={sortByOptions}
          />

          {!!onToggleBlockedOrSuspended && (
            <FormControl display="flex" alignItems="center" width="auto">
              <Switch
                id="blocked-or-suspended-filter"
                isChecked={isBlockedOrSuspended}
                onChange={(event) =>
                  onToggleBlockedOrSuspended(event.target.checked)
                }
              />
              <FormLabel
                htmlFor="blocked-or-suspended-filter"
                mb="0"
                ml="2"
                whiteSpace="nowrap"
              >
                Bloqueados/suspensos
              </FormLabel>
            </FormControl>
          )}

          {!!onToggleShowDeleted && (
            <FormControl display="flex" alignItems="center" width="auto">
              <Switch
                id="show-deleted-member-filter"
                isChecked={showDeleted}
                onChange={() => onToggleShowDeleted()}
              />
              <FormLabel
                htmlFor="show-deleted-member-filter"
                mb="0"
                ml="2"
                whiteSpace="nowrap"
              >
                Removidos
              </FormLabel>
            </FormControl>
          )}
        </HStack>

        {typeof totalItems === 'number' && (
          <Text fontSize="lg">{`Total: ${totalItems}`}</Text>
        )}
      </Flex>

      <Table colorScheme="blue" mt="4">
        <Thead>
          <Tr>
            <Th w="8" textAlign="center">
              Status
            </Th>

            <Th>Membro</Th>

            <Th>Experiência</Th>

            <Th>Telefone</Th>

            <Th>E-mail</Th>

            <Th>CPF</Th>

            <Th textAlign="center">Data de cadastro</Th>

            <Th textAlign="right" w="8" />
          </Tr>
        </Thead>

        <Tbody>
          {members.map((member) => {
            const memberOnboard = member.onboards.find(
              (onboard) => onboard.ventureId === authenticatedVenture?.id,
            );
            const memberAssignment =
              member.userAssignments.find(
                (assignment) =>
                  assignment.ventureId === authenticatedVenture?.id,
              ) ?? member.userAssignments[0];
            const isBlocked = !!memberAssignment?.isBlocked;
            const isSuspended = !!memberAssignment?.blockedUntil;
            const restriction =
              member.effectiveRestriction ??
              (isBlocked || isSuspended
                ? {
                    blockedUntil: memberAssignment?.blockedUntil,
                    isBlocked,
                    source: 'SELF' as const,
                    sourceUserId: member.id,
                    sourceUserName: member.name,
                  }
                : undefined);
            const restrictionLabel = restriction
              ? getRestrictionLabel(restriction)
              : '';

            return (
              <Tr key={member.id}>
                <Td>
                  <VStack align="stretch" spacing="1">
                    <HStack justifyContent="space-between">
                      <Text>Face id:</Text>

                      <FaceIdStatusBadge
                        faceIdStatus={member.faceIdStatus}
                        avatarStatus={member.avatarStatus}
                      />
                    </HStack>

                    <HStack justifyContent="space-between">
                      <Text>Cadastro:</Text>

                      <AccountStatusBadge
                        confirmPending={member.confirmPending}
                      />
                    </HStack>

                    {!!memberOnboard && (
                      <HStack justifyContent="space-between">
                        <Text>Onboard:</Text>

                        <OnboardStatusBadge
                          onboardType={memberOnboard.onboardType}
                        />
                      </HStack>
                    )}

                    {!!restriction && (
                      <HStack justifyContent="space-between">
                        <Text>Restrição:</Text>

                        <Tooltip
                          placement="bottom"
                          hasArrow
                          label={restrictionLabel}
                        >
                          <span>
                            <Icon
                              as={PiWarningCircle}
                              color={
                                restriction.isBlocked ? 'red.400' : 'orange.400'
                              }
                              fontSize={26}
                              cursor="pointer"
                            />
                          </span>
                        </Tooltip>
                      </HStack>
                    )}
                  </VStack>
                </Td>

                <Td>
                  <Box>
                    <Text fontWeight="bold">{member.name}</Text>
                    <Text textColor="gray.400">
                      {memberAssignment?.featureGroup?.name}
                    </Text>
                  </Box>
                </Td>

                <Td>
                  {memberOnboard?.onboardType === 'COMPLETED'
                    ? experienceLevelsTitleMap.get(memberOnboard.experience)
                    : 'Não avaliado'}
                </Td>

                <Td>{member.phone}</Td>

                <Td>{member.email}</Td>

                <Td>{member.cpf}</Td>

                <Td textAlign="center">{member.formattedDate}</Td>

                <Td textAlign="right">
                  <HStack justifyContent="flex-end" spacing="2">
                    {!showDeleted && (
                      <LinkButton
                        px={0}
                        to={{
                          pathname: '/members/details',
                          state: {
                            memberId: member.id,
                          },
                        }}
                      >
                        <Icon as={RiEyeLine} fontSize="16" />
                      </LinkButton>
                    )}

                    <VentureAssignmentActions
                      user={member}
                      onChanged={() => onAssignmentChanged?.()}
                    />
                  </HStack>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>

      {!!currentPage && !!onPageChange && !!totalPages && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      )}
    </Box>
  );
};
